import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchInventoryAuditLog,
  fetchInventoryAuditUsers,
  type InventoryAuditAction,
  type InventoryAuditChange,
  type InventoryAuditEntityType,
  type InventoryAuditEntry,
} from '../api/inventoryAuditLog';

/**
 * The inventory change log: every product and unit write, newest first, with the
 * user who made it and a field-level before/after.
 *
 * Entries come straight from the server's inventory_audit_log — nothing here is
 * derived from the current inventory, so a row that was deleted still shows what
 * it looked like when it went.
 */

const PAGE_SIZE = 50;

/** Collapsed rows show a few fields; the rest are behind a per-row toggle. */
const COLLAPSED_CHANGE_COUNT = 3;

const ACTION_LABELS: Record<InventoryAuditAction, string> = {
  created: 'Added',
  updated: 'Edited',
  deleted: 'Removed',
};

const SOURCE_LABELS: Record<string, string> = {
  inventory: 'Inventory',
  shipment: 'Incoming shipment',
  installation: 'Installation',
};

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const ChangeList: React.FC<{ action: InventoryAuditAction; changes: InventoryAuditChange[] }> = ({
  action,
  changes,
}) => {
  const [expanded, setExpanded] = useState(false);
  if (changes.length === 0) {
    return <span className="inventory-log-muted">No field details recorded.</span>;
  }
  const shown = expanded ? changes : changes.slice(0, COLLAPSED_CHANGE_COUNT);
  const hidden = changes.length - shown.length;
  return (
    <>
      <ul className="inventory-log-changes">
        {shown.map((c) => (
          <li key={c.field}>
            <span className="inventory-log-field">{c.label}:</span>{' '}
            {action === 'updated' ? (
              <>
                <span className="inventory-log-value inventory-log-value--old">{c.from ?? '—'}</span>
                <span className="inventory-log-arrow" aria-hidden="true"> → </span>
                <span className="inventory-log-value">{c.to ?? '—'}</span>
              </>
            ) : (
              <span className="inventory-log-value">
                {(action === 'created' ? c.to : c.from) ?? '—'}
              </span>
            )}
          </li>
        ))}
      </ul>
      {hidden > 0 && (
        <button type="button" className="inventory-log-more" onClick={() => setExpanded(true)}>
          +{hidden} more field{hidden === 1 ? '' : 's'}
        </button>
      )}
      {expanded && changes.length > COLLAPSED_CHANGE_COUNT && (
        <button type="button" className="inventory-log-more" onClick={() => setExpanded(false)}>
          Show less
        </button>
      )}
    </>
  );
};

const InventoryLogTab: React.FC = () => {
  const [entries, setEntries] = useState<InventoryAuditEntry[]>([]);
  const [nextCursor, setNextCursor] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<Array<{ id: number; name: string }>>([]);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [entityType, setEntityType] = useState<InventoryAuditEntityType | ''>('');
  const [action, setAction] = useState<InventoryAuditAction | ''>('');
  const [userId, setUserId] = useState<number | ''>('');

  // Typing shouldn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const filters = useMemo(
    () => ({ search, entityType, action, userId: userId === '' ? null : userId }),
    [search, entityType, action, userId]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const page = await fetchInventoryAuditLog({ ...filters, limit: PAGE_SIZE });
      setEntries(page.entries);
      setNextCursor(page.nextCursor);
    } catch (err) {
      setEntries([]);
      setNextCursor(null);
      setError(err instanceof Error ? err.message : 'Failed to load the inventory log.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    fetchInventoryAuditUsers()
      .then(setUsers)
      .catch(() => setUsers([]));
  }, []);

  const loadMore = useCallback(async () => {
    if (nextCursor == null) return;
    setLoadingMore(true);
    try {
      const page = await fetchInventoryAuditLog({ ...filters, limit: PAGE_SIZE, cursor: nextCursor });
      setEntries((prev) => [...prev, ...page.entries]);
      setNextCursor(page.nextCursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more entries.');
    } finally {
      setLoadingMore(false);
    }
  }, [filters, nextCursor]);

  const filtersActive = search !== '' || entityType !== '' || action !== '' || userId !== '';

  return (
    <div className="inventory-table-section">
      <div className="inventory-log-toolbar">
        <input
          type="text"
          className="page-toolbar-search"
          placeholder="Search by user, serial, product, or value..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          aria-label="Search the inventory log"
        />
        <select
          className="inventory-log-select"
          value={entityType}
          onChange={(e) => setEntityType(e.target.value as InventoryAuditEntityType | '')}
          aria-label="Filter by what changed"
        >
          <option value="">Products &amp; items</option>
          <option value="product">Products only</option>
          <option value="unit">Items only</option>
        </select>
        <select
          className="inventory-log-select"
          value={action}
          onChange={(e) => setAction(e.target.value as InventoryAuditAction | '')}
          aria-label="Filter by change type"
        >
          <option value="">All changes</option>
          <option value="created">Added</option>
          <option value="updated">Edited</option>
          <option value="deleted">Removed</option>
        </select>
        <select
          className="inventory-log-select"
          value={userId}
          onChange={(e) => setUserId(e.target.value === '' ? '' : Number(e.target.value))}
          aria-label="Filter by user"
        >
          <option value="">Anyone</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </select>
        <button type="button" className="page-toolbar-btn" onClick={() => void load()} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && <p className="inventory-log-error">{error}</p>}

      <div className="inventory-table-wrapper">
        <table className="inventory-table inventory-log-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Changed by</th>
              <th>What</th>
              <th>Change</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.id}>
                <td data-label="When" className="inventory-log-when">
                  {formatDateTime(entry.occurredAt)}
                </td>
                <td data-label="Changed by">
                  {entry.userName ?? <span className="inventory-log-muted">Not recorded</span>}
                </td>
                <td data-label="What">
                  <span className="inventory-log-entity">
                    {entry.entityType === 'product' ? 'Product' : 'Item'}
                  </span>{' '}
                  {entry.entityLabel ?? <span className="inventory-log-muted">(no serial)</span>}
                  {entry.entityType === 'unit' && entry.productName && (
                    <div className="inventory-log-subtle">{entry.productName}</div>
                  )}
                </td>
                <td data-label="Change">
                  <span className={`inventory-log-action inventory-log-action--${entry.action}`}>
                    {ACTION_LABELS[entry.action] ?? entry.action}
                  </span>
                  <ChangeList action={entry.action} changes={entry.changes} />
                </td>
                <td data-label="Source">
                  {entry.source ? SOURCE_LABELS[entry.source] ?? entry.source : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!loading && entries.length === 0 && !error && (
        <p className="page-subtitle">
          {filtersActive
            ? 'No log entries match these filters.'
            : 'No inventory changes have been logged yet. Changes appear here as soon as someone edits a product or an item.'}
        </p>
      )}

      {nextCursor != null && (
        <div className="inventory-log-footer">
          <button type="button" className="page-toolbar-btn" onClick={() => void loadMore()} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
};

export default InventoryLogTab;
